import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { getToken, setToken, setUnauthorizedHandler } from "@/api/client";
import { authApi } from "@/api/endpoints";
import type { User } from "@/api/types";

interface AuthState {
  user: User | null;
  /** True until the stored token has been validated against /auth/me. */
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  /**
   * Adopt a session obtained outside the login form - specifically the one
   * returned by /auth/password/set, so a user who just chose a password is not
   * bounced to the login screen to retype it.
   */
  adoptSession: (token: string, user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  // A token in localStorage is not proof of a valid session - it may be expired
  // or signed with a rotated secret, so it is verified once on mount.
  useEffect(() => {
    let cancelled = false;

    async function restore() {
      if (!getToken()) {
        setLoading(false);
        return;
      }
      try {
        const me = await authApi.me();
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) setToken(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  // Any 401 from any request drops the session, so an expired token cannot
  // leave the app rendering a shell it can no longer load data for.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    setToken(response.access_token);
    setUser(response.user);
  }, []);

  const adoptSession = useCallback((token: string, nextUser: User) => {
    setToken(token);
    setUser(nextUser);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, adoptSession, logout }),
    [user, loading, login, adoptSession, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext };
